import React, { useRef, useMemo, useEffect } from 'react';
import {
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalCloseButton, Text,
} from '@chakra-ui/react';
import { MapContainer, TileLayer, Marker, Circle, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

const DraggableMarker = ({ position, onChange }: { position: [number, number]; onChange: (lat: number, lng: number) => void }) => {
  const markerRef = useRef<L.Marker>(null);
  const handlers = useMemo(() => ({
    dragend() {
      const m = markerRef.current;
      if (m) { const p = m.getLatLng(); onChange(p.lat, p.lng); }
    },
  }), [onChange]);
  return <Marker draggable position={position} eventHandlers={handlers} ref={markerRef} />;
};

const MapClickHandler = ({ onClick }: { onClick: (lat: number, lng: number) => void }) => {
  useMapEvents({ click(e) { onClick(e.latlng.lat, e.latlng.lng); } });
  return null;
};

interface Props {
  isOpen: boolean;
  onClose: () => void;
  lat: number;
  lng: number;
  rangeInKm: number;
  areaName: string;
  onChange: (lat: number, lng: number) => void;
}

const MapPreviewModal: React.FC<Props> = ({ isOpen, onClose, lat, lng, rangeInKm, areaName, onChange }) => {
  const mapRef = useRef<L.Map>(null);

  useEffect(() => {
    if (isOpen) setTimeout(() => mapRef.current?.invalidateSize(), 150);
  }, [isOpen]);

  if (!lat || !lng) return null;

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl">
      <ModalOverlay />
      <ModalContent maxW="680px">
        <ModalHeader bg="teal.600" color="white" borderTopRadius="md" fontSize="md">
          📍 {areaName} — {rangeInKm} km radius
        </ModalHeader>
        <ModalCloseButton color="white" />
        <ModalBody p={0}>
          <MapContainer center={[lat, lng]} zoom={13} style={{ height: '420px', width: '100%' }} ref={mapRef}>
            <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" attribution="&copy; OpenStreetMap / Leaflet" />
            <MapClickHandler onClick={onChange} />
            <DraggableMarker position={[lat, lng]} onChange={onChange} />
            <Circle center={[lat, lng]} radius={rangeInKm * 1000} pathOptions={{ color: 'teal', fillColor: 'teal', fillOpacity: 0.15 }} />
          </MapContainer>
          <Text fontSize="xs" color="gray.500" p={2} textAlign="center">
            Click on map or drag marker to adjust. Lat: {lat.toFixed(5)}, Lng: {lng.toFixed(5)}
          </Text>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default MapPreviewModal;
